import React from "react";
import { Shell } from "@/components/shell";
import { Heading } from "@/components/ui/heading";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Mail, MapPin, Phone } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import SEO from "@/components/SEO";

export default function ContactPage() {
  const { toast } = useToast();
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Hier würde normalerweise der API-Aufruf zum Senden der Kontaktformulardaten stehen
    // z.B.: await fetch('/api/contact', {method: 'POST', body: JSON.stringify(formData)})
    
    toast({
      title: "Nachricht gesendet",
      description: "Vielen Dank für Ihre Nachricht. Wir werden uns so schnell wie möglich bei Ihnen melden.",
      variant: "default",
    });

    // Formular zurücksetzen
    const form = e.target as HTMLFormElement;
    form.reset();
  };

  return (
    <>
      <SEO title="Kontakt" description="Kontaktiere uns – wir helfen dir gerne weiter." />
      <Shell>
      <div className="container px-4 py-8 mx-auto max-w-5xl">
        <Heading 
          title="Kontakt" 
          description="Haben Sie Fragen oder Anregungen? Kontaktieren Sie uns."
        />
        
        <div className="grid md:grid-cols-3 gap-8 mt-8">
          <div className="md:col-span-1 space-y-6">
            <Card className="p-6">
              <div className="flex flex-col space-y-4">
                <div className="flex items-start">
                  <MapPin className="w-5 h-5 mt-1 mr-3 text-primary" />
                  <div>
                    <h3 className="font-medium">Anschrift</h3>
                    <address className="not-italic text-muted-foreground mt-1">
                      SpeiseGuru GmbH<br />
                      Musterstraße 123<br />
                      12345 Musterstadt
                    </address>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Mail className="w-5 h-5 mt-1 mr-3 text-primary" />
                  <div>
                    <h3 className="font-medium">E-Mail</h3>
                    <p className="text-muted-foreground mt-1">
                      <a href="mailto:info@speiseguru.de" className="hover:text-primary">
                        info@speiseguru.de
                      </a>
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Phone className="w-5 h-5 mt-1 mr-3 text-primary" />
                  <div>
                    <h3 className="font-medium">Telefon</h3>
                    <p className="text-muted-foreground mt-1">
                      <a href="tel:+491234567890" className="hover:text-primary">
                        +49 (0) 123 456 7890
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </Card>
            
            <Card className="p-6">
              <h3 className="font-medium mb-2">Öffnungszeiten Support</h3>
              <div className="text-muted-foreground space-y-1">
                <p className="flex justify-between">
                  <span>Montag - Freitag:</span>
                  <span>9:00 - 17:00 Uhr</span>
                </p>
                <p className="flex justify-between">
                  <span>Samstag:</span>
                  <span>10:00 - 14:00 Uhr</span>
                </p>
                <p className="flex justify-between">
                  <span>Sonntag:</span>
                  <span>Geschlossen</span>
                </p>
              </div>
            </Card>
          </div>
          
          <Card className="p-6 md:col-span-2">
            <h2 className="text-xl font-semibold mb-4">Schreiben Sie uns</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" name="name" placeholder="Ihr Name" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail</Label>
                  <Input id="email" name="email" type="email" placeholder="ihre-email@beispiel.de" required />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="subject">Betreff</Label>
                <Input id="subject" name="subject" placeholder="Betreff Ihrer Nachricht" required />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="message">Nachricht</Label>
                <Textarea 
                  id="message" 
                  name="message" 
                  placeholder="Ihre Nachricht an uns..." 
                  rows={6} 
                  required 
                />
              </div>
              
              <div className="mt-4">
                <Button type="submit" className="w-full sm:w-auto">
                  Nachricht senden
                </Button>
              </div>
            </form>
          </Card>
        </div>
        
        <div className="mt-12">
          <Card className="p-0 overflow-hidden">
            <div className="aspect-video w-full">
              {/* Hier könnte eine Google Maps Einbettung stehen oder ein Bild des Standorts */}
              <div className="w-full h-full bg-muted flex items-center justify-center">
                <p className="text-muted-foreground">
                  Kartenansicht wird hier eingebettet
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </Shell>
    </>
  );
}